import React, { useState, useEffect } from "react";
import { X, Smartphone, Lock, LogIn } from "lucide-react";
import { Flame } from "lucide-react";
import { sendOtp, verifyOtp, createCourseOrder, verifyCoursePayment } from "../api/api";
import { storeAuthData, getAuthData } from "../api/storageService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CoursePurchasePopup = ({ 
    open, 
    onClose, 
    course, 
    couponCode = null,  // Make sure this is defined
    couponData = null,  // Make sure this is defined
    finalPrice = null   // Make sure this is defined
}) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [step, setStep] = useState('phone');
    const [authMode, setAuthMode] = useState('register');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [paymentProcessed, setPaymentProcessed] = useState(false);
    const [paymentProcessingStarted, setPaymentProcessingStarted] = useState(false);

    // Registration fields
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [otpSent, setOtpSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [isVerified, setIsVerified] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(true);
    const [userData, setUserData] = useState(null);

    // Log the props to debug
    console.log("CoursePurchasePopup props:", { couponCode, couponData, finalPrice, courseId: course?.id });

    // Check if user is logged in and process payment directly
    useEffect(() => {
        const authData = getAuthData();
        if (authData && authData.token) {
            setIsLoggedIn(true);
            setUserData(authData);
            // Only process if not already processed and not started
            if (open && !isProcessingPayment && !paymentProcessed && !paymentProcessingStarted) {
                setPaymentProcessingStarted(true);
                processDirectPayment(authData);
            }
        }
        // Reset flags when popup closes
        if (!open) {
            setPaymentProcessingStarted(false);
            setPaymentProcessed(false);
        }
    }, [open]);

    const startResendTimer = (duration) => {
        setResendTimer(duration);
        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value && index < 3) {
                document.getElementById(`otp-${index + 1}`)?.focus();
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`)?.focus();
        }
    };

    const processDirectPayment = async (user) => {
        if (isProcessingPayment || paymentProcessed || paymentProcessingStarted) {
            console.log("Payment already in progress or processed");
            return;
        }

        setPaymentProcessingStarted(true);
        setIsProcessingPayment(true);

        try {
            const userName = user.name || "User";
            const userEmail = user.email || "user@example.com";

            // IMPORTANT: Use couponCode from props
            const couponToSend = couponCode || ""; // Use prop or empty string
            console.log("Sending coupon code in order:", couponToSend);

            const orderResponse = await createCourseOrder({
                course_ids: [course?.id],
                coupon_code: couponToSend,  // THIS IS THE KEY FIX
                name: userName,
                email: userEmail
            });

            console.log("Order Response:", orderResponse);

            // Check if user already owns the course
            if (orderResponse.success === false) {
                toast.error(orderResponse.message || "You already own this course", {
                    position: "top-right",
                    autoClose: 3000,
                });
                setIsProcessingPayment(false);
                setPaymentProcessingStarted(false);
                setTimeout(() => {
                    onClose();
                }, 3000);
                return;
            }

            if (!orderResponse.order) {
                toast.error("Failed to create order. Please try again.");
                setIsProcessingPayment(false);
                setPaymentProcessingStarted(false);
                return;
            }

            const order = orderResponse.order;
            const options = {
                key: order.razorpay_key,
                amount: order.amount,
                currency: order.currency || "INR",
                name: "Nutrillion Academy",
                description: "Course Purchase",
                order_id: order.razorpay_order_id,
                handler: async function (paymentResponse) {
                    try {
                        const verifyResponse = await verifyCoursePayment({
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_payment_id: paymentResponse.razorpay_payment_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                            order_id: order.id
                        });
                        if (verifyResponse.status || verifyResponse.success) {
                            toast.success("Payment Successful 🎉");
                            setPaymentProcessed(true);
                            setTimeout(() => {
                                onClose();
                            }, 2000);
                        } else {
                            toast.error("Payment verification failed");
                        }
                    } catch (verifyError) {
                        console.error("Verification error:", verifyError);
                        toast.error("Payment verification failed");
                    } finally {
                        setIsProcessingPayment(false);
                        setPaymentProcessingStarted(false);
                    }
                },
                prefill: {
                    name: userName,
                    email: userEmail,
                    contact: user.phone || ""
                },
                theme: {
                    color: "#16a34a"
                },
                modal: {
                    ondismiss: function () {
                        setIsProcessingPayment(false);
                        setPaymentProcessingStarted(false);
                    }
                }
            };

            // Initialize Razorpay with error handling
            const initRazorpay = () => {
                try {
                    const razorpay = new window.Razorpay(options);
                    razorpay.open();
                } catch (razorpayError) {
                    console.error("Razorpay initialization error:", razorpayError);
                    toast.error("Payment gateway error. Please try again.");
                    setIsProcessingPayment(false);
                    setPaymentProcessingStarted(false);
                }
            };

            if (!window.Razorpay) {
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = initRazorpay;
                script.onerror = () => {
                    toast.error("Failed to load payment gateway. Please try again.");
                    setIsProcessingPayment(false);
                    setPaymentProcessingStarted(false);
                };
                document.body.appendChild(script);
            } else {
                initRazorpay();
            }

        } catch (error) {
            console.error("Payment error:", error);
            // Check if it's an "already own" error from API
            const errorMessage = error?.response?.data?.message || error?.message || "";
            if (errorMessage.includes("already own") || errorMessage.includes("already purchased")) {
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                });
            } else {
                toast.error("Payment failed. Please try again.");
            }
            setIsProcessingPayment(false);
            setPaymentProcessingStarted(false);
        }
    };

    // Send OTP for registration/login
    const handleSendOTP = async () => {
        if (authMode === 'register') {
            if (!firstName.trim()) {
                toast.error("Please enter your first name");
                return;
            }
            if (!agreeTerms) {
                toast.error("Please agree to Terms of Service and Privacy Policy");
                return;
            }
        }

        if (phoneNumber.length < 10) {
            toast.error("Enter valid 10 digit phone number");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await sendOtp(phoneNumber, "weblogin");
            if (response.status) {
                setOtpSent(true);
                setStep('otp');
                toast.success("OTP sent successfully ✅");
                startResendTimer(60);
                setOtp(["", "", "", ""]);
                setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
            } else {
                toast.error(response.message || "Failed to send OTP");
            }
        } catch (error) {
            console.error("Send OTP error:", error);
            toast.error("Failed to send OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Verify OTP and register/login user
    const handleVerifyOTP = async () => {
        const otpString = otp.join("");
        if (otpString.length < 4) {
            toast.error("Please enter a valid 4-digit OTP");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await verifyOtp(phoneNumber, otpString, "weblogin");
            if (response.status) {
                storeAuthData(response);
                setIsVerified(true);
                setUserData(response.data);

                if (authMode === 'register') {
                    toast.success("Registration successful 🎉");
                    setSuccess("Account created successfully!");
                } else {
                    toast.success("Login successful 🎉");
                    setSuccess("Welcome back!");
                }

                // Process payment only once after verification
                if (!paymentProcessed && !paymentProcessingStarted) {
                    setPaymentProcessingStarted(true);
                    await processDirectPayment(response.data);
                }

            } else {
                toast.error(response.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            console.error("Verify OTP error:", error);
            toast.error("Failed to verify OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOTP = () => {
        if (resendTimer === 0) {
            handleSendOTP();
        }
    };

    const handlePayment = async () => {
        const user = userData || getAuthData();
        if (!user || !user.token) {
            toast.error("Please login first");
            return;
        }
        // Prevent double payment
        if (!paymentProcessed && !paymentProcessingStarted) {
            setPaymentProcessingStarted(true);
            await processDirectPayment(user);
        }
    };

    // Get display price with coupon
    const getDisplayPrice = () => {
        if (finalPrice !== null && finalPrice !== undefined) {
            return `₹${finalPrice}`;
        }
        // Use coupon data if available
        if (couponData && couponData.final_amount) {
            return `₹${couponData.final_amount}`;
        }
        return course?.discounted_price_formatted || course?.price_formatted || "₹0";
    };

    const getOriginalDisplayPrice = () => {
        if (course?.discounted_price) {
            return course?.price_formatted;
        }
        if (finalPrice !== null && course?.price) {
            return course?.price_formatted;
        }
        return null;
    };

    if (!open) return null;

    return (
        <>
            <ToastContainer 
                position="top-right" 
                autoClose={3000} 
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            
            {!isLoggedIn && (
                <div className="fixed font-lato inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
                    <div className="popup-scroll w-full md:max-w-[650px] max-w-[95%] bg-white rounded-lg shadow-xl relative max-h-[100vh] overflow-y-auto">
                        <button
                            onClick={onClose}
                            className="absolute right-3 top-3 bg-white rounded-full w-6 h-6 text-gray-700 z-10 flex items-center justify-center"
                        >
                            <X size={16} />
                        </button>

                        {/* Show coupon applied banner */}
                        {couponCode && couponData && (
                            <div className="bg-green-100 border-b border-green-300 px-5 py-3">
                                <p className="text-sm text-green-700 font-medium">
                                    ✓ Coupon <strong>{couponCode}</strong> applied! 
                                    {couponData.discount_type === 'percentage' 
                                        ? ` ${couponData.discount_value}% off` 
                                        : couponData.discount_value ? ` ₹${couponData.discount_value} off` : ''}
                                    {couponData.final_amount && ` • Final Price: ₹${couponData.final_amount}`}
                                </p>
                            </div>
                        )}

                        <div className="bg-gradient-to-br from-[#003b25] to-[#092d2b] px-5 py-5 text-white">
                            <div className="flex items-center gap-1 text-[14px] tracking-wider text-green-400 font-bold">
                                {authMode === 'login' ? "LOGIN & PURCHASE" : "REGISTER & PURCHASE"}
                            </div>
                            <h2 className="mt-3 text-[22px] font-bold text-green-300">
                                {authMode === 'login' ? "Login to Purchase" : "Create Account & Get Access"}
                            </h2>
                            <p className="text-[14px] text-gray-300 mt-1 leading-4">
                                {authMode === 'login'
                                    ? "Login to your account and complete your purchase."
                                    : "Register now to unlock this course and start learning."}
                            </p>
                        </div>

                        <div className="p-4">
                            {/* Price Display with coupon info */}
                           {/* Price Display with coupon info */}
<div className="bg-gray-50 rounded-lg p-3 mb-3 border">
    <div className="flex flex-col md:flex-row md:justify-between md:items-center">
        <div>
            <p className="text-sm font-medium text-gray-700">{course?.title}</p>
            <p className="text-xs text-gray-500">One-time purchase · Lifetime access</p>
            {couponCode && (
                <p className="text-xs text-green-600 mt-1">
                    Coupon: <strong>{couponCode}</strong> applied
                </p>
            )}
        </div>
        <div className="text-left md:text-right mt-2 md:mt-0">
            <p className="text-xl font-bold text-green-600">
                {getDisplayPrice()}
            </p>
            {getOriginalDisplayPrice() && (
                <p className="text-xs text-gray-400 line-through">
                    {getOriginalDisplayPrice()}
                </p>
            )}
        </div>
    </div>
</div>

                            <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                <div className="flex gap-2 items-center">
                                    <div className="bg-green-600 text-white p-2 rounded">
                                        {authMode === 'login' ? <LogIn size={15} /> : <Smartphone size={15} />}
                                    </div>
                                    <div>
                                        <h4 className="text-[16px] font-bold text-green-700">
                                            {authMode === 'login' ? "Login to Your Account" : "Mobile Verification"}
                                        </h4>
                                        <p className="text-[14px] text-green-500">
                                            {step === 'phone'
                                                ? (authMode === 'login' ? 'Enter your phone number to login' : 'Enter your details to get started')
                                                : 'Enter OTP sent to your phone'}
                                        </p>
                                    </div>
                                </div>

                                {error && (
                                    <div className="mt-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-[12px]">
                                        {error}
                                    </div>
                                )}
                                {success && (
                                    <div className="mt-2 bg-green-50 border border-green-200 text-green-700 p-2 rounded text-[12px]">
                                        {success}
                                    </div>
                                )}

                                {step === 'phone' ? (
                                    <>
                                        {authMode === 'register' && (
                                            <>
                                                <div className="grid grid-cols-2 gap-2 mt-3">
                                                    <input
                                                        placeholder="First name *"
                                                        className="border rounded px-3 py-2 text-[14px]"
                                                        value={firstName}
                                                        onChange={(e) => setFirstName(e.target.value)}
                                                    />
                                                    <input
                                                        placeholder="Last name"
                                                        className="border rounded px-3 py-2 text-[14px]"
                                                        value={lastName}
                                                        onChange={(e) => setLastName(e.target.value)}
                                                    />
                                                </div>
                                                <input
                                                    placeholder="Email address"
                                                    className="border rounded px-3 py-2 text-[14px] w-full mt-3"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                            </>
                                        )}

                                        <div className={`flex mt-3 gap-1 ${authMode === 'login' ? 'mt-1' : ''}`}>
                                            <div className="border rounded bg-white px-2 py-2 text-[14px]">
                                                +91
                                            </div>
                                            <input
                                                placeholder="Enter mobile number"
                                                className="flex-1 border rounded px-2 text-[14px] outline-none"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                                                maxLength={10}
                                            />
                                            <button
                                                onClick={handleSendOTP}
                                                disabled={isLoading || phoneNumber.length < 10}
                                                className="bg-green-600 text-white rounded px-3 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            >
                                                {isLoading ? "Sending..." : "Send OTP"}
                                            </button>
                                        </div>

                                        {authMode === 'register' && (
                                            <div className="flex items-start gap-2 mt-3 text-[12px] text-gray-500">
                                                <label className="flex items-start gap-2 cursor-pointer flex-shrink-0 mt-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={agreeTerms}
                                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                                        className="hidden"
                                                    />
                                                    <span
                                                        className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${agreeTerms
                                                            ? "bg-green-600 text-white"
                                                            : "border border-gray-300"
                                                            }`}
                                                    >
                                                        {agreeTerms && "✓"}
                                                    </span>
                                                </label>
                                                <div>
                                                    I agree to the{" "}
                                                    <button
                                                        onClick={() => window.open('/terms-conditions', '_blank')}
                                                        className="text-green-600 hover:underline"
                                                        type="button"
                                                    >
                                                        Terms of Service
                                                    </button>{" "}
                                                    and{" "}
                                                    <button
                                                        onClick={() => window.open('/privacy-policy', '_blank')}
                                                        className="text-green-600 hover:underline"
                                                        type="button"
                                                    >
                                                        Privacy Policy
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-4 text-center text-[13px] text-gray-600">
                                            {authMode === 'register' ? (
                                                <>
                                                    Already have an account?{" "}
                                                    <button
                                                        onClick={() => {
                                                            setAuthMode('login');
                                                            setFirstName("");
                                                            setLastName("");
                                                            setEmail("");
                                                            setPhoneNumber("");
                                                            setOtp(["", "", "", ""]);
                                                            setOtpSent(false);
                                                            setStep('phone');
                                                            setError("");
                                                            setSuccess("");
                                                            setPaymentProcessed(false);
                                                            setPaymentProcessingStarted(false);
                                                        }}
                                                        className="text-green-600 font-semibold hover:underline"
                                                    >
                                                        Login here
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    Don't have an account?{" "}
                                                    <button
                                                        onClick={() => {
                                                            setAuthMode('register');
                                                            setFirstName("");
                                                            setLastName("");
                                                            setEmail("");
                                                            setPhoneNumber("");
                                                            setOtp(["", "", "", ""]);
                                                            setOtpSent(false);
                                                            setStep('phone');
                                                            setError("");
                                                            setSuccess("");
                                                            setPaymentProcessed(false);
                                                            setPaymentProcessingStarted(false);
                                                        }}
                                                        className="text-green-600 font-semibold hover:underline"
                                                    >
                                                        Create one
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-[12px] text-green-700 mt-2">
                                            Enter 4-digit OTP sent to +91 {phoneNumber}
                                        </p>

                                        <div className="flex justify-between mt-2">
                                            <div className="flex gap-2">
                                                {otp.map((digit, index) => (
                                                    <input
                                                        key={index}
                                                        id={`otp-${index}`}
                                                        type="text"
                                                        maxLength="1"
                                                        className="w-12 h-12 text-center text-[18px] font-bold bg-white border rounded-md focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
                                                        value={digit}
                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                    />
                                                ))}
                                            </div>
                                            <button
                                                onClick={handleVerifyOTP}
                                                disabled={isLoading}
                                                className="bg-green-600 text-white rounded px-4 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isLoading ? "Verifying..." : authMode === 'login' ? "Login" : "Verify"}
                                            </button>
                                        </div>

                                        <div className="flex justify-between items-center mt-3">
                                            <span className="text-[12px] text-gray-400">
                                                Didn't get it?
                                                {resendTimer > 0 ? (
                                                    ` Resend in ${resendTimer}s`
                                                ) : (
                                                    <button
                                                        onClick={handleResendOTP}
                                                        className="text-green-600 underline ml-1"
                                                    >
                                                        Resend OTP
                                                    </button>
                                                )}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setStep('phone');
                                                setOtp(["", "", "", ""]);
                                                setOtpSent(false);
                                            }}
                                            className="mt-3 text-[12px] text-gray-500 hover:text-gray-700 underline"
                                        >
                                            ← Go back
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CoursePurchasePopup;